import { CommandProps } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import {
   addColumn as addColumnUtility,
   addRow as addRowUtility,
   CellSelection,
   isInTable,
   selectedRect,
   TableMap,
} from '@tiptap/pm/tables';
import { findTable, isInTableMap } from './utils/table_utils';
import {
   deleteRow as deleteRowProsemirrorCommand,
   mergeCells as mergeCellsProsemirrorCommand,
} from './utils/prosemirror_commands';

/**
 * Selects the row at `rowIndex` in the current table as determined
 * by the current `selection` as context. If no table is found the
 * command does nothing.
 */
export function selectRow(rowIndex: number) {
   return ({ tr, dispatch }: CommandProps) => {
      const table = findTable(tr.selection);
      if (!table) {
         return false;
      }
      const map = TableMap.get(table.node);

      if (!isInTableMap(rowIndex, 0, map)) {
         return false;
      }

      const relativePosition = map.positionAt(rowIndex, 0, table.node);
      const position = relativePosition + table.start;

      if (dispatch) {
         const resolvedPos = tr.doc.resolve(position);
         const rowSelection = CellSelection.rowSelection(resolvedPos);
         tr.setSelection(rowSelection);
      }

      return true;
   };
}

/**
 * Selects the column at `columnIndex` in the current table as determined
 * by the current `selection` as context. If no table is found the
 * command does nothing.
 */
export function selectColumn(columnIndex: number) {
   return ({ tr, dispatch }: CommandProps) => {
      const table = findTable(tr.selection);
      if (!table) {
         return false;
      }
      const map = TableMap.get(table.node);

      if (!isInTableMap(0, columnIndex, map)) {
         return false;
      }

      const relativePosition = map.positionAt(0, columnIndex, table.node);
      const position = relativePosition + table.start;

      if (dispatch) {
         const resolvedPos = tr.doc.resolve(position);
         const colSelection = CellSelection.colSelection(resolvedPos);
         tr.setSelection(colSelection);
      }

      return true;
   };
}

/**
 * Select the current table as determined by the current `selection`.
 */
export function selectTable() {
   return ({ tr, dispatch, commands }: CommandProps) => {
      const table = findTable(tr.selection);

      if (!table) {
         return false;
      }

      if (dispatch) {
         commands.setNodeSelection(table.pos);
      }

      return true;
   };
}

/**
 * Wraps the `deleteColumn` command. The original command leaves the
 * selection in a mangled state, so this makes sure to return to a
 * reasonable selection state.
 *
 * Specifically, this commands returns to the start of the next column if
 * possible, and the end of previous column if not.
 */
export function cleanDeleteColumn() {
   return ({ tr, dispatch, commands, state }: CommandProps) => {
      const rect = selectedRect(state);

      commands.deleteColumn();

      let table = findTable(tr.selection);

      if (!table) {
         return false;
      }
      if (!dispatch) {
         return true;
      }

      const tableMap = TableMap.get(table.node);

      // Delete the column but return the selection to a reasonable state
      if (isInTableMap(0, rect.right, tableMap)) {
         const next = tableMap.positionAt(0, rect.left, table.node);
         const resolvedPos = tr.doc.resolve(table.start + next);
         return commands.setTextSelection(TextSelection.near(resolvedPos));
      } else if (isInTableMap(rect.bottom - 1, rect.left - 1, tableMap)) {
         const previous = tableMap.positionAt(rect.bottom - 1, rect.left - 1, table.node);
         const resolvedPos = tr.doc.resolve(table.start + previous);
         return commands.setTextSelection(TextSelection.near(resolvedPos));
      } else {
         return false;
      }
   };
}

/**
 * Deletes the current row from the table.
 *
 * Wraps our custom `deleteRow` prosemirror command that fixes a bug in the original
 */
export function deleteRow() {
   return ({ state, dispatch }: CommandProps) => {
      return deleteRowProsemirrorCommand(state, dispatch);
   };
}

/**
 * Add a row at `rowIndex` in the current table. Does nothing if not in a table.
 */
export function addRow(rowIndex: number) {
   return ({ state, dispatch }: CommandProps) => {
      if (!isInTable(state)) {
         return false;
      }
      if (dispatch) {
         const rect = selectedRect(state);
         dispatch(addRowUtility(state.tr, rect, rowIndex));
      }
      return true;
   };
}

/**
 * Add a column at `columnIndex` in the current table. Does nothing if not in a table.
 */
export function addColumn(columnIndex: number) {
   return ({ state, dispatch }: CommandProps) => {
      if (!isInTable(state)) {
         return false;
      }
      if (dispatch) {
         const rect = selectedRect(state);
         dispatch(addColumnUtility(state.tr, rect, columnIndex));
      }
      return true;
   };
}

export function mergeCells() {
   return ({ state, dispatch }: CommandProps) => {
      return mergeCellsProsemirrorCommand(state, dispatch);
   };
}
